import { createElement, Fragment, memo, useMemo, useState, useCallback, useEffect, Children, isValidElement, cloneElement } from 'react';
import { MediaQuery, Link, DateTime, Image, Util, Button, Carousel, LazyImage, Accordion, AccordionItem } from '@grandvision/common';
import { Container, Heading, Text } from '@grandvision/elements';
import classnames from 'classnames';
import { useInView } from 'react-intersection-observer';
import { Link as Link$1 } from 'react-scroll';
import { Icon } from '@grandvision/icons';
import ReactHtmlParser from 'react-html-parser';
import ReactPlayer from 'react-player';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0
THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.
See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

var Picture = function (_a) {
    var url = _a.url, mobileUrl = _a.mobileUrl, alt = _a.alt, className = _a.className;
    return (createElement("picture", { className: classnames('picture', className) },
        mobileUrl && (createElement("source", { media: "(max-width: " + MediaQuery.Breakpoints.M + "px)", srcSet: mobileUrl })),
        createElement("source", { media: "(min-width: " + MediaQuery.Breakpoints.M + ")", srcSet: url }),
        createElement("img", { className: "picture__image", src: url, alt: alt })));
};

var LazyPicture = function (_a) {
    var image = _a.image, mobileImage = _a.mobileImage, className = _a.className;
    var isPhone = MediaQuery.useIsPhone();
    var _b = isPhone && mobileImage ? mobileImage.dimensions : image.dimensions, h = _b.h, w = _b.w;
    var _c = __read(useInView({
        rootMargin: '150px 0px',
        triggerOnce: true,
    }), 2), ref = _c[0], inView = _c[1];
    return (createElement("div", { ref: ref, className: classnames('lazy-picture', className), style: { paddingBottom: ((h / w) * 100).toFixed(3) + "%" } }, inView && (createElement(Picture, { className: "lazy-picture__image", mobileUrl: mobileImage === null || mobileImage === void 0 ? void 0 : mobileImage.url, url: image.url, alt: image.alt }))));
};

var Banner = function (_a) {
    var image = _a.image, mobileImage = _a.mobileImage, link = _a.link, _b = _a.useContainer, useContainer = _b === void 0 ? false : _b, _c = _a.useLazyLoading, useLazyLoading = _c === void 0 ? true : _c;
    var ContainerComponent = useContainer ? Container : Fragment;
    if (!image) {
        return null;
    }
    return (createElement("div", { className: "banner" },
        createElement(ContainerComponent, null,
            useLazyLoading ? (createElement(LazyPicture, { mobileImage: mobileImage, image: image })) : (createElement(Picture, { mobileUrl: mobileImage === null || mobileImage === void 0 ? void 0 : mobileImage.url, url: image.url, alt: image.alt })),
            link && (createElement(Link, { hidden: true, url: link.url, target: link.target, className: "banner__link" }, link.label && createElement("span", { className: "banner__link-text" }, link.label))))));
};

var BlogMeta = function (_a) {
    var publishDate = _a.publishDate, _b = _a.readingTime, readingTime = _b === void 0 ? 0 : _b, minutesLabel = _a.minutesLabel, locale = _a.locale;
    var hasReadingTime = readingTime && readingTime > 0 && minutesLabel;
    if (!publishDate) {
        return null;
    }
    return (createElement("div", { className: "blog-meta" },
        createElement("span", { className: "blog-meta__date" },
            createElement(DateTime, { dateTime: publishDate, locale: locale })),
        hasReadingTime && (createElement("span", { className: "blog-meta__minutes" }, "- " + readingTime.toString() + " " + minutesLabel))));
};

var BlogCard = function (_a) {
    var image = _a.image, mobileImage = _a.mobileImage, title = _a.title, locale = _a.locale, readingTime = _a.readingTime, publishDate = _a.publishDate, summary = _a.summary, url = _a.url, minutesLabel = _a.minutesLabel;
    var isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    var imageUrl = isSmallScreen && mobileImage ? mobileImage.url : image === null || image === void 0 ? void 0 : image.url;
    return (createElement("a", { className: "blog-card", href: url },
        createElement("div", { className: "blog-card__content" },
            image && imageUrl && (createElement(Image, { className: "blog-card__image", url: imageUrl, alt: image.alt, cover: true })),
            createElement("div", { className: "blog-card__text" },
                createElement(Heading, { component: "h3", className: "blog-card__title" }, Util.truncateString(title, 50)),
                summary && createElement("p", { className: "blog-card__paragraph" }, Util.truncateString(summary, 200)),
                createElement(BlogMeta, { publishDate: publishDate, readingTime: readingTime, minutesLabel: minutesLabel, locale: locale })))));
};
var BlogCard$1 = memo(BlogCard);

// fixed navigation height needed for anchor scrolling
var siteNavHeightOffset = -64;
var BlogDetailHeader = function (_a) {
    var image = _a.image, mobileImage = _a.mobileImage, title = _a.title, scrollTo = _a.scrollTo;
    var isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    var imageUrl = isSmallScreen && mobileImage ? mobileImage.url : image.url;
    if (!imageUrl) {
        return null;
    }
    return (createElement("div", { className: "blog-detail-header", style: { backgroundImage: "url(" + imageUrl + ")" } },
        createElement("h1", { className: "blog-detail-header__title" }, title),
        createElement(Link$1, { className: "blog-detail-header__link", href: "#", to: scrollTo, offset: siteNavHeightOffset, smooth: true, duration: 500 },
            createElement(Icon, { name: "chevron-down" }))));
};

var BlogHero = function (_a) {
    var image = _a.image, mobileImage = _a.mobileImage, title = _a.title, summary = _a.summary, link = _a.link, readMoreLabel = _a.readMoreLabel;
    var _b;
    var isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    var imageUrl = isSmallScreen && mobileImage ? mobileImage.url : (_b = image === null || image === void 0 ? void 0 : image.url) !== null && _b !== void 0 ? _b : '';
    var textLength = isSmallScreen ? 100 : 150;
    return (createElement("div", { className: "blog-hero", style: { backgroundImage: "url(" + imageUrl + ")" } },
        createElement(Container, { className: "site-container" },
            createElement("div", { className: "blog-hero__content" },
                createElement(Heading, { component: "h1", className: "blog-hero__title" }, title),
                summary && (createElement(Text, { className: "blog-hero__intro" }, Util.truncateString(summary, textLength))),
                createElement(Button, { size: "medium", href: link, className: "blog-hero__button", variant: "outline", caption: readMoreLabel, "aria-label": readMoreLabel })))));
};
var BlogHero$1 = memo(BlogHero);

var BlogIntro = function (_a) {
    var summary = _a.summary, publishDate = _a.publishDate, readingTime = _a.readingTime, minutesLabel = _a.minutesLabel, locale = _a.locale;
    return (createElement("div", { className: "blog-intro" },
        summary && createElement("p", { className: "blog-intro__summary" }, summary),
        createElement(BlogMeta, { publishDate: publishDate, readingTime: readingTime, minutesLabel: minutesLabel, locale: locale })));
};

var BlogList = function (_a) {
    var title = _a.title, children = _a.children, noArticlesLabel = _a.noArticlesLabel;
    return (createElement("div", { className: "blog-list" },
        createElement(Heading, { component: "h2", className: "blog-list__title" }, title),
        children ? (createElement("div", { className: "blog-list__wrapper" }, children)) : (createElement(Heading, { variant: "smallest", className: "blog-list__message" }, noArticlesLabel))));
};

var BrandList = function (_a) {
    var title = _a.title, children = _a.children, className = _a.className;
    return (createElement("div", { className: classnames('brand-list', className) },
        createElement("h3", { className: "heading brand-list__title" }, title),
        createElement("ul", { className: "brand-list__content" }, children)));
};

var BrandListGroup = function (_a) {
    var title = _a.title, children = _a.children, className = _a.className;
    return (createElement("li", { className: classnames('brand-list-group', className) },
        createElement("h5", { className: classnames('brand-list-group__title', {
                'brand-list-group__title--empty': !(title === null || title === void 0 ? void 0 : title.length),
            }) }, title),
        children));
};

var BrandListItem = function (_a) {
    var url = _a.url, children = _a.children, className = _a.className;
    return (createElement("a", { className: classnames('brand-list-item', className), href: url }, children));
};
var BrandListItem$1 = memo(BrandListItem);

var ExclusiveBrands = function (_a) {
    var children = _a.children;
    return (createElement("div", { className: "exclusive-brands" },
        createElement("ul", { className: "exclusive-brands__brand-list" }, children)));
};

var ExclusiveBrandsItem = function (_a) {
    var logoSrc = _a.logoSrc, title = _a.title, url = _a.url;
    return (createElement("li", { className: "exclusive-brands-item" },
        createElement("a", { className: "exclusive-brands-item__link", href: url },
            createElement("img", { className: "exclusive-brands-item__image", src: logoSrc, alt: title }))));
};
var ExclusiveBrandsItem$1 = memo(ExclusiveBrandsItem);

var SUPPORTED_STYLES = ['primary', 'secondary', 'primary outline', 'secondary outline'];
var ContentBanner = function (_a) {
    var image = _a.image, mobileImage = _a.mobileImage, title = _a.title, text = _a.text, links = _a.links, _b = _a.textColor, textColor = _b === void 0 ? 'white' : _b;
    var isPhone = MediaQuery.useIsPhone();
    var imageUrl = isPhone ? mobileImage.url : image.url;
    var imageLink = links.find(function (_a) {
        var style = _a.style;
        return style === 'image';
    });
    var _c = __read(useInView({
        rootMargin: '150px 0px',
        triggerOnce: true,
    }), 2), ref = _c[0], inView = _c[1];
    var btnLinks = useMemo(function () {
        return links
            .filter(function (_a) {
            var style = _a.style;
            return SUPPORTED_STYLES.includes(style !== null && style !== void 0 ? style : '');
        })
            .map(function (_a, i) {
            var url = _a.url, label = _a.label, style = _a.style, target = _a.target;
            return (createElement(Button, __assign({ key: i, href: url, caption: label, className: "content-banner__button", hrefTarget: target === 'external' ? '_blank' : undefined }, Util.createButtonStyles(style))));
        });
    }, [links]);
    if (!image) {
        return null;
    }
    return (createElement("div", { ref: ref, className: "content-banner content-banner--color-" + textColor, style: inView ? { backgroundImage: "url(" + imageUrl + ")" } : undefined },
        createElement(Container, { className: "content-banner__container" },
            createElement("div", { className: "content-banner__body" },
                title && (createElement(Heading, { className: "content-banner__title", component: "h2", variant: "large" }, title)),
                text && createElement(Text, { className: "content-banner__text" }, text),
                btnLinks,
                imageLink && (createElement(Link, { hidden: true, url: imageLink.url, target: imageLink.target, className: "content-banner__image-link" }, imageLink.label && (createElement("span", { className: "content-banner__image-link-text" }, imageLink.label))))))));
};

var HeadTags = function (_a) {
    var description = _a.description, ogUrl = _a.ogUrl, ogImage = _a.ogImage, _b = _a.headComponent, Component = _b === void 0 ? Fragment : _b, title = _a.title, hreflangs = _a.hreflangs, noFollow = _a.noFollow, noIndex = _a.noIndex, children = _a.children;
    return (createElement(Component, null,
        createElement("title", null, title),
        ogUrl && createElement("link", { rel: "canonical", href: ogUrl }),
        hreflangs.map(function (_a) {
            var url = _a.url, locale = _a.locale;
            return (createElement("link", { rel: "alternate", key: url, hrefLang: locale, href: url }));
        }),
        createElement("meta", { name: "robots", content: (noIndex ? 'noindex' : 'index') + ", " + (noFollow ? 'nofollow' : 'follow') }),
        createElement("meta", { property: "og:url", content: ogUrl }),
        createElement("meta", { property: "og:title", content: title }),
        createElement("meta", { name: "description", content: description }),
        createElement("meta", { property: "og:description", content: description }),
        createElement("meta", { name: "twitter:site", content: ogUrl }),
        createElement("meta", { name: "twitter:card", content: "summary_large_image" }),
        createElement("meta", { name: "twitter:image", content: ogImage }),
        createElement("meta", { property: "og:image", content: ogImage }),
        createElement("meta", { property: "og:image:width", content: "1200" }),
        createElement("meta", { property: "og:image:height", content: "630" }),
        children));
};

var regHex = /#([a-f0-9]{3}){1,2}\b/i;
var regPresetBg = /black|grey|white/;
var RichBanner = function (_a) {
    var _b;
    var image = _a.image, mobileImage = _a.mobileImage, link = _a.link, richText = _a.richText, layout = _a.layout, bannerWidth = _a.bannerWidth, imageWidth = _a.imageWidth, _c = _a.imageAlignment, imageAlignment = _c === void 0 ? 'left' : _c, _d = _a.backgroundColor, backgroundColor = _d === void 0 ? 'transparent' : _d, _e = _a.textColor, textColor = _e === void 0 ? 'black' : _e;
    var ContainerComponent = bannerWidth === 'contained' ? Container : Fragment;
    var classNames = classnames('rich-banner', "rich-banner--image-alignment-" + imageAlignment, "rich-banner--text-color-" + textColor, (_b = {},
        _b["rich-banner--image-width-" + imageWidth] = imageWidth,
        _b["rich-banner--layout-" + layout] = layout,
        _b));
    var richBannerStyle = {
        backgroundColor: regHex.test(backgroundColor)
            ? backgroundColor
            : regPresetBg.test(backgroundColor)
                ? Util.colorHexMap[backgroundColor]
                : 'transparent',
    };
    return (createElement(ContainerComponent, null,
        createElement("div", { className: classNames, style: richBannerStyle },
            image && (createElement("main", { className: "rich-banner__slot rich-banner__slot--primary" }, link ? (createElement(Link, { className: "rich-banner__link", url: link.url, target: link.target },
                link.label && createElement("span", { className: "rich-banner__link-text" }, link.label),
                createElement(LazyPicture, { className: "rich-banner__picture", mobileImage: mobileImage, image: image }))) : (createElement(LazyPicture, { className: "rich-banner__picture", mobileImage: mobileImage, image: image })))),
            richText && (createElement("aside", { className: "rich-banner__slot rich-banner__slot--secondary" }, bannerWidth !== 'contained' && imageWidth === '100' ? (createElement(Container, null,
                createElement("div", { className: "rich-banner__content" }, ReactHtmlParser(richText)))) : (createElement("div", { className: "rich-banner__content" }, ReactHtmlParser(richText))))))));
};

var CategoryLinks = function (_a) {
    var children = _a.children;
    var isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    if (!children || !Array.isArray(children)) {
        return null;
    }
    return (createElement("article", { className: "category-links" },
        createElement(Container, null,
            createElement("div", { className: "category-links__content" }, isSmallScreen ? createElement(Carousel, { pagination: true }, children) : children))));
};

var CategoryLinksItem = function (_a) {
    var title = _a.title, link = _a.link, image = _a.image;
    return (createElement("section", { className: "category-links-item" },
        createElement("a", { className: "category-links-item__link", href: link.url },
            createElement("div", { className: "category-links-item__image-container" }, image && (createElement(LazyImage, { rootMargin: "50px 0px", src: image.url, alt: image.alt || '', width: image.dimensions.w || 274, height: image.dimensions.h || 108 }))),
            title && createElement("span", { className: "category-links-item__title" }, title))));
};

var Certificates = function (_a) {
    var children = _a.children;
    if (!children) {
        return null;
    }
    return (createElement("div", { className: "certificates" },
        createElement(Container, null,
            createElement("article", { className: "certificates__list" }, children))));
};

var CertificatesItem = function (_a) {
    var link = _a.link, image = _a.image, title = _a.title, text = _a.text;
    var _b;
    var LinkElement = !!link ? 'a' : 'div';
    return (createElement("section", { className: classnames('certificates-item', {
            'certificates-item--center': !title && !text,
        }) },
        createElement(LinkElement, { className: "certificates-item__container", href: (link === null || link === void 0 ? void 0 : link.url) || undefined },
            image && (createElement("img", { className: "certificates-item__image", src: image.url, alt: (_b = image.alt) !== null && _b !== void 0 ? _b : '' })),
            (title || text) && (createElement("div", { className: "certificates-item__content" },
                title && createElement("h4", { className: "certificates-item__title" }, title),
                text && createElement("p", { className: "certificates-item__description" }, text))))));
};

var PromotionBanners = function (_a) {
    var children = _a.children, _b = _a.fullWidth, fullWidth = _b === void 0 ? false : _b;
    if (!children) {
        return null;
    }
    return (createElement("div", { className: classnames('promotion-banners', {
            'promotion-banners--full-width': fullWidth,
        }) },
        createElement(Container, { className: "promotion-banners__container" }, children)));
};

var PromotionBannersItem = function (_a) {
    var image = _a.image, mobileImage = _a.mobileImage, link = _a.link, title = _a.title, text = _a.text, _b = _a.fullWidth, fullWidth = _b === void 0 ? false : _b;
    return (createElement("a", { className: classnames('promotion-banners-item', {
            'promotion-banners-item--overlay': title || text,
            'promotion-banners-item--full-width': fullWidth && title && text,
        }), href: link.url },
        image && image.url && (createElement(LazyPicture, { className: "promotion-banners-item__image", image: image, mobileImage: mobileImage })),
        createElement("div", { className: "promotion-banners-item__body" },
            (title || text) && (createElement("div", { className: "promotion-banners-item__content" },
                title && createElement("h3", { className: "promotion-banners-item__title" }, title),
                text && createElement("p", { className: "promotion-banners-item__description" }, text))),
            link && link.label && (createElement(Button, __assign({ size: "custom", caption: link.label, className: "promotion-banners-item__button" }, Util.createButtonStyles(link.style)))))));
};

var Faq = function (_a) {
    var name = _a.name, title = _a.title, ids = _a.ids, children = _a.children;
    var isPhone = MediaQuery.useIsPhone();
    var _b = __read(useState(), 2), activeId = _b[0], setActiveId = _b[1];
    var handleTitleClick = useCallback(function () {
        history.replaceState(null, '', "#" + name);
    }, [name]);
    // Handle hash from url on mount
    useEffect(function () {
        var id = window.location.hash.replace(/^#/, '');
        var hasItem = ids.find(function (idx) { return idx === id; }) || name === id;
        hasItem && setActiveId(id);
    }, []);
    useEffect(function () {
        if (activeId) {
            // Push onto callback queue so it runs after the DOM is updated
            setTimeout(function () {
                var node = document.getElementById(activeId);
                if (node) {
                    var headerOffset = 200;
                    var top_1 = node.getBoundingClientRect().top;
                    var to = Math.round(top_1 + window.pageYOffset) - headerOffset;
                    if (to > headerOffset) {
                        window.scrollTo(0, to);
                    }
                }
                // If the active hash is not the title, focus the related te control element
                if (activeId !== name) {
                    var ctrlNode = document.getElementById(activeId + "-control");
                    ctrlNode && ctrlNode.focus();
                }
            }, 250);
        }
    }, [activeId]);
    return (createElement(Container, null,
        createElement("button", { className: "faq-title-wrapper", onClick: handleTitleClick, "aria-label": title },
            createElement(Heading, { id: name, component: "h2", variant: isPhone ? 'small' : 'medium' }, title)),
        createElement(Accordion, { className: "faq" }, Children.map(children, function (child) { return isValidElement(child) && cloneElement(child, { activeId: activeId }); }))));
};

var FaqItem = function (_a) {
    var id = _a.id, question = _a.question, answer = _a.answer, activeId = _a.activeId;
    var handleClick = useCallback(function (expanded) {
        if (expanded && id) {
            history.replaceState(null, '', "#" + id);
        }
    }, [id]);
    return (createElement(AccordionItem, { key: id, id: id, headingLevel: "h3", label: question, expanded: id === activeId, onClick: handleClick }, ReactHtmlParser(answer)));
};

var HighlightedContent = function (_a) {
    var children = _a.children, className = _a.className;
    if (!children) {
        return null;
    }
    return (createElement("div", { className: classnames('highlighted-content', className) },
        createElement("div", { className: "highlighted-content__inner" },
            createElement("article", { className: "highlighted-content__items" }, children))));
};

var HighlightedContentItem = function (_a) {
    var image = _a.image, link = _a.link, title = _a.title, text = _a.text;
    var LinkElement = !!link ? 'a' : 'div';
    return (createElement("section", { className: classnames('highlighted-content-item', {
            'highlighted-content-item__with-link': !!link,
        }) },
        createElement(LinkElement, { className: "highlighted-content-item__link", href: (link === null || link === void 0 ? void 0 : link.url) || undefined },
            image && (createElement("div", { className: "highlighted-content-item__image", style: { backgroundImage: "url(" + image.url + ")" } })),
            createElement("div", { className: "highlighted-content-item__content" },
                title && (createElement(Heading, { variant: "medium", className: "highlighted-content-item__title" }, Util.truncateString(title, 50))),
                text && (createElement("p", { className: "highlighted-content-item__description" }, Util.truncateString(text, 200)))))));
};

var IconListItem = function (_a) {
    var icon = _a.icon, label = _a.label, url = _a.url;
    var WrapperElement = url ? Link : 'div';
    return (createElement("li", { className: "icon-list-item" },
        createElement(WrapperElement, { className: "icon-list-item__inner", url: url },
            createElement(Icon, { name: icon }),
            label && createElement("span", { className: "icon-list-item__text" }, label))));
};

var IconList = function (_a) {
    var title = _a.title, _b = _a.items, items = _b === void 0 ? [] : _b, _c = _a.theme, theme = _c === void 0 ? 'bar' : _c, className = _a.className;
    return (createElement("article", { className: classnames('icon-list', className, {
            'icon-list--tiles': theme === 'tiles',
        }) },
        createElement("div", { className: "site-container" },
            title && (createElement(Heading, { component: "h3", className: "icon-list__title" }, title)),
            createElement("ul", { className: "icon-list__items" }, items.map(function (item, index) { return (createElement(IconListItem, __assign({}, item, { key: item.icon + "-" + index }))); })))));
};
var IconList$1 = memo(IconList);

var UspGroupItem = function (_a) {
    var item = _a.item, className = _a.className;
    var icon = item.icon, text = item.text;
    var classNames = classnames('usp-group-item', className);
    return (createElement("div", { className: classNames },
        icon && (createElement("span", { className: "usp-group-item__icon" },
            createElement(Icon, { name: icon, width: 18, height: 18 }))),
        createElement("span", { className: "usp-group-item__message" }, text)));
};

var UspGroup = function (_a) {
    var policyItems = _a.policyItems, theme = _a.theme;
    if (!policyItems.length) {
        return null;
    }
    var isSolaris = theme === 'sunglasses' ? 'usp-group--solaris' : '';
    return (createElement("div", { className: "usp-group " + isSolaris },
        createElement("div", { className: "usp-group__container desktop-hidden" },
            createElement(Carousel, { navigation: true, inverseColor: true, pagination: false }, policyItems.map(function (item) { return (createElement(UspGroupItem, { item: item, key: item.text })); }))),
        createElement("div", { className: "usp-group__container mobile-hidden" }, policyItems.map(function (item) { return (createElement(UspGroupItem, { item: item, key: item.text })); }))));
};

var Video = memo(function (_a) {
    var videoUrl = _a.videoUrl, coverImage = _a.coverImage;
    var playerConfig = {
        height: '100%',
        width: '100%',
        controls: true,
        playing: true,
    };
    var _b = __read(useState(false), 2), playing = _b[0], setPlaying = _b[1];
    var handleClick = useCallback(function () { return setPlaying(true); }, [setPlaying]);
    if (!videoUrl || !coverImage) {
        return null;
    }
    return (createElement("div", { className: "video", role: "button", onClick: handleClick },
        createElement("div", { className: "video__wrapper" }, playing ? (createElement("div", { className: "video__player" },
            createElement(ReactPlayer, __assign({ url: videoUrl }, playerConfig)))) : (createElement("div", { className: "video__image", style: { backgroundImage: "url(" + (coverImage.url || '') + ")" } },
            createElement("button", { className: "video__play-button" },
                createElement(Icon, { name: "play" })))))));
});

export { Banner, BlogCard$1 as BlogCard, BlogDetailHeader, BlogHero$1 as BlogHero, BlogIntro, BlogList, BlogMeta, BrandList, BrandListGroup, BrandListItem$1 as BrandListItem, CategoryLinks, CategoryLinksItem, Certificates, CertificatesItem, ContentBanner, ExclusiveBrands, ExclusiveBrandsItem$1 as ExclusiveBrandsItem, Faq, FaqItem, HeadTags, HighlightedContent, HighlightedContentItem, IconList$1 as IconList, IconListItem, LazyPicture, Picture, PromotionBanners, PromotionBannersItem, RichBanner, UspGroup, Video };
//# sourceMappingURL=index.es.js.map
